import RibbonButton from './RibbonButton';
import RibbonPlugin from '../../plugin/RibbonPlugin';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import loc from 'owa-localize';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import * as React from 'react';
import { createLink } from 'roosterjs-editor-api';
import { Editor } from 'roosterjs-editor-core';
import { QueryScope } from 'roosterjs-editor-types';
import {
    createLink as createLink_1,
    linkTitle,
    displayTextLabel,
    urlLabel,
} from './insertLink.locstring.json';

let styles = require('./insertLink.scss');

const IMAGE_NODE_NAME = 'IMG';
const TEXT_NODE_NAME = '#text';

const insertLink: RibbonButton = {
    key: 'insertLink',
    title: () => loc(createLink_1),
    imageSrc: require('../svg/createlink.svg'),
    // The actual keyboard shortcut handling logic is in RibbonPlugin.
    // These strings, in most cases, are documenting behaviour that already exists in
    // Rooster or is implemented by a plugin.
    shortcut: 'ctrl+K',
    onExecute: showInsertLinkDialog,
};

export default insertLink;
export interface InsertLinkProperties {
    DisplayText: string;
    LinkUrl: string;
    HasChanged: boolean;
    SelectionContent?: DocumentFragment;
}

export async function displayInsertLinkDialog(
    setLink: InsertLinkProperties
): Promise<DialogResponse> {
    let linkInput: HTMLInputElement;
    let displayTextInput: HTMLInputElement;

    const onLinkTitleInputChange = () => {
        setLink.DisplayText = displayTextInput.value;
        setLink.HasChanged = true;
    };

    const onLinkHrefInputChange = () => {
        if (displayTextInput && displayTextInput.value == setLink.LinkUrl) {
            setLink.DisplayText = linkInput.value;
            displayTextInput.value = linkInput.value;
        }
        setLink.LinkUrl = linkInput.value;
        setLink.HasChanged = true;
    };

    return await confirm(loc(linkTitle), null /*subText*/, false /*resolveImmediately*/, {
        okText: loc(okButton),
        cancelText: loc(cancelButton),
        bodyElement: (
            <div className={styles.linkContent}>
                <div className={styles.displayTextInputBox}>
                    <label htmlFor="displayTextInput">{loc(displayTextLabel)}</label>
                    {setLink.SelectionContent ? (
                        <div id="displayTextInput" className={styles.linkDisplay}>
                            {renderDisplayNodes(setLink.SelectionContent.childNodes)}
                        </div>
                    ) : (
                        <input
                            id="displayTextInput"
                            role="textbox"
                            type="text"
                            ref={ref => (displayTextInput = ref)}
                            onChange={onLinkTitleInputChange}
                            defaultValue={setLink.DisplayText}
                            className={styles.linkInput}
                        />
                    )}
                </div>
                <div className={styles.linkInputBox}>
                    <label htmlFor="linkInput">{loc(urlLabel)}</label>
                    <input
                        id="linkInput"
                        role="textbox"
                        type="text"
                        ref={ref => (linkInput = ref)}
                        onChange={onLinkHrefInputChange}
                        defaultValue={setLink.LinkUrl}
                        className={styles.linkInput}
                    />
                </div>
            </div>
        ),
        delayCallbackAfterAnimation: true,
        containerClassName: styles.linkDialog,
        focusSelector: '#linkInput',
    });
}

export async function showInsertLinkDialog(plugin: RibbonPlugin) {
    let editor = plugin.getEditor();
    let range = editor.getSelectionRange();
    let insertLink = await createInsertLink(plugin, editor, range);

    plugin.workaroundFocusIssueForFirefox();

    if (DialogResponse.ok === (await displayInsertLinkDialog(insertLink))) {
        editor.select(range);
        if (insertLink.HasChanged) {
            createLink(
                editor,
                insertLink.LinkUrl,
                insertLink.LinkUrl,
                insertLink.DisplayText.length > 0 ? insertLink.DisplayText : null
            );
        }
    } else {
        editor.select(range);
    }
}

async function createInsertLink(
    plugin: RibbonPlugin,
    editor: Editor,
    range: Range
): Promise<InsertLinkProperties> {
    let insertLink: InsertLinkProperties = {
        LinkUrl: '',
        DisplayText: '',
        HasChanged: false,
    };

    // Check if there is an image within the selection
    const imgNode = await getSelectedImageNode(plugin, editor);
    if (imgNode) {
        if (!range.intersectsNode(imgNode)) {
            // If an image is selected but not within the selection range,
            // add it to the range
            range.selectNode(imgNode);
        }
        const rangeContent = range.cloneContents();
        if (insertLink.SelectionContent !== rangeContent) {
            insertLink.HasChanged = true;
        }
        insertLink.SelectionContent = rangeContent;
    }

    // Check if there is an existing hyperlink within the selection
    // If so, the default text of "Web address" input should match the existing link
    const linkElements = editor.queryElements('a[href]', QueryScope.OnSelection);
    const linkNode =
        linkElements && linkElements.length > 0 && (linkElements[0] as HTMLAnchorElement);
    if (linkNode && range.intersectsNode(linkNode)) {
        insertLink.LinkUrl = linkNode.href;
    }

    if (!insertLink.SelectionContent) {
        // If no image is selected, set DisplayText to put a default value in the "Display as" input
        // If the selection is within a link element, the default text should be the text of the link element
        // Otherwise, the default text should be empty
        insertLink.DisplayText = linkNode ? linkNode.textContent : range.toString();
    }

    return insertLink;
}

async function getSelectedImageNode(
    plugin: RibbonPlugin,
    editor: Editor
): Promise<HTMLImageElement> {
    let imgNode = await plugin.getImageSelectedByPlugin();
    if (!imgNode) {
        const imgElements = editor.queryElements('img', QueryScope.OnSelection);
        imgNode = imgElements && imgElements.length > 0 && (imgElements[0] as HTMLImageElement);
    }
    return imgNode;
}

function renderDisplayNodes(nodes: NodeListOf<ChildNode>): JSX.Element {
    const nodeArray = nodes ? Array.prototype.slice.call(nodes) : null;
    return nodeArray && <div>{nodeArray.map(node => renderDisplayNode(node))}</div>;
}

function renderDisplayNode(node: Node): JSX.Element {
    return node.childNodes && node.childNodes.length > 0 ? (
        renderDisplayNodes(node.childNodes)
    ) : node.nodeName === IMAGE_NODE_NAME ? (
        <img className={styles.linkDisplayImage} src={(node as HTMLImageElement).src} />
    ) : node.nodeName === TEXT_NODE_NAME ? (
        <span>{node.textContent}</span>
    ) : null;
}
